// src/marketing/launch/LaunchPage.tsx
import { useState, useMemo } from 'react'
import { useAppStore } from '../../shell/appStore'
import { ApiClientError } from '../../lib/api'
import type { LaunchTask } from '../../lib/types'
import { useLaunchPlans, useCreateLaunchPlan, useDeleteLaunchPlan } from './hooks/useLaunchPlans'
import { useLaunchTasks, usePatchLaunchTask, useCreateLaunchTask, useDeleteLaunchTask } from './hooks/useLaunchTasks'
import { MilestoneGroup } from './MilestoneGroup'
import { NewLaunchDialog } from './NewLaunchDialog'
import { Countdown } from '../editorial/Countdown'
import { MilestoneTimeline } from '../editorial/MilestoneTimeline'
import { launchTaskDate } from './dates'

type EditTaskState = {
  planId: string
  task: LaunchTask | null
  milestone: string
  milestoneOrder: number
}

export function LaunchPage() {
  const appId = useAppStore((s) => s.activeAppId)
  const [selectedPlanId, setSelectedPlanId] = useState<string | null>(null)
  const [showNewDialog, setShowNewDialog] = useState(false)
  const [dialogError, setDialogError] = useState<string | null>(null)
  const [toast, setToast] = useState<string | null>(null)
  const [editTask, setEditTask] = useState<EditTaskState | null>(null)

  const { data: plans = [], isLoading: plansLoading } = useLaunchPlans(appId)
  const activePlanId = selectedPlanId ?? plans[0]?.id ?? null
  const activePlan = plans.find((p) => p.id === activePlanId) ?? null

  const { data: tasks = [], isLoading: tasksLoading } = useLaunchTasks(activePlanId)

  const createPlan = useCreateLaunchPlan()
  const deletePlan = useDeleteLaunchPlan()
  const patchTask = usePatchLaunchTask(activePlanId)
  const createTask = useCreateLaunchTask()
  const deleteTask = useDeleteLaunchTask(activePlanId)

  const milestoneGroups = useMemo(() => {
    const groups = new Map<number, { milestone: string; milestoneOrder: number; tasks: LaunchTask[] }>()
    for (const t of tasks) {
      if (!groups.has(t.milestoneOrder)) {
        groups.set(t.milestoneOrder, { milestone: t.milestone, milestoneOrder: t.milestoneOrder, tasks: [] })
      }
      groups.get(t.milestoneOrder)!.tasks.push(t)
    }
    const sorted = [...groups.values()].sort((a, b) => a.milestoneOrder - b.milestoneOrder)
    for (const g of sorted) {
      g.tasks.sort((a, b) => a.sortOrder - b.sortOrder)
    }
    return sorted
  }, [tasks])

  // Jalons pour la MilestoneTimeline : 1 jalon par groupe, date = première tâche ancrée
  const timelineMilestones = useMemo(() => {
    const todayStr = new Date().toISOString().slice(0, 10)
    return milestoneGroups.map((g) => {
      const anchoredTask = g.tasks.find((t) => t.dayOffset !== null)
      const date = anchoredTask
        ? launchTaskDate(activePlan?.launchDate ?? null, anchoredTask.dayOffset)
        : null
      const current = date !== null && date <= todayStr
      return { label: g.milestone, date, current }
    })
  }, [milestoneGroups, activePlan])

  function showToast(msg: string) {
    setToast(msg)
    setTimeout(() => setToast(null), 4000)
  }

  function handleToggle(id: string, done: boolean) {
    patchTask.mutate(
      { id, data: { done } },
      {
        onError: (err) => {
          showToast(err instanceof ApiClientError ? err.message : 'Erreur lors de la mise à jour')
        },
      },
    )
  }

  function handleDeleteTask(id: string) {
    deleteTask.mutate(id, {
      onError: (err) => {
        showToast(err instanceof ApiClientError ? err.message : 'Erreur lors de la suppression')
      },
    })
  }

  function handleAddTask(milestone: string, milestoneOrder: number) {
    setEditTask({ planId: activePlanId!, task: null, milestone, milestoneOrder })
  }

  function handleEditTask(task: LaunchTask) {
    setEditTask({ planId: activePlanId!, task, milestone: task.milestone, milestoneOrder: task.milestoneOrder })
  }

  function handleCreatePlan(name: string, launchDate: string | null, duplicateOf?: string | null) {
    if (!appId) return
    setDialogError(null)
    createPlan.mutate(
      { appId, name, launchDate, duplicateOf: duplicateOf ?? null },
      {
        onSuccess: (result) => {
          setShowNewDialog(false)
          setSelectedPlanId(result.plan.id)
        },
        onError: (err) => {
          setDialogError(err instanceof ApiClientError ? err.message : 'Erreur inattendue')
        },
      },
    )
  }

  function handleDeletePlan() {
    if (!activePlanId) return
    deletePlan.mutate(activePlanId, {
      onSuccess: () => {
        setSelectedPlanId(null)
        showToast('Plan supprimé')
      },
      onError: (err) => {
        showToast(err instanceof ApiClientError ? err.message : 'Erreur lors de la suppression')
      },
    })
  }

  if (!appId) {
    return (
      <div
        data-testid="marketing-launch"
        style={{ padding: '2rem', color: 'var(--muted)', fontSize: '0.875rem' }}
      >
        Sélectionnez une app pour gérer les lancements.
      </div>
    )
  }

  return (
    <div data-testid="marketing-launch" style={{ padding: '1.5rem 2rem', maxWidth: '860px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
        <h1 style={{ fontSize: '1.25rem', fontWeight: 600, color: 'var(--fg)', margin: 0 }}>
          Suivi de lancement
        </h1>
        <div style={{ flex: 1 }} />
        <button
          onClick={() => { setShowNewDialog(true); setDialogError(null) }}
          style={btnPrimary}
        >
          + Nouveau lancement
        </button>
      </div>

      {plans.length > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1.25rem', flexWrap: 'wrap' }}>
          <select
            value={activePlanId ?? ''}
            onChange={(e) => setSelectedPlanId(e.target.value || null)}
            aria-label="Sélectionner un plan"
            style={{
              padding: '0.375rem 0.625rem',
              background: 'var(--surface)',
              border: '1px solid var(--line)',
              borderRadius: '6px',
              fontSize: '0.875rem',
              color: 'var(--fg)',
              fontFamily: 'inherit',
              cursor: 'pointer',
            }}
          >
            {plans.map((p) => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
          {activePlan?.launchDate && (
            <span style={{ fontSize: '0.8125rem', color: 'var(--muted)' }}>
              D0 : <strong style={{ color: 'var(--fg)' }}>{activePlan.launchDate}</strong>
            </span>
          )}
          <button
            onClick={handleDeletePlan}
            style={{ ...btnSecondary, color: 'var(--arancio)', marginLeft: 'auto' }}
          >
            Supprimer ce plan
          </button>
        </div>
      )}

      {plansLoading || tasksLoading ? (
        <div style={{ color: 'var(--muted)', fontSize: '0.875rem' }}>Chargement…</div>
      ) : plans.length === 0 ? (
        <div style={{
          padding: '3rem 2rem',
          textAlign: 'center',
          color: 'var(--muted)',
          fontSize: '0.875rem',
          background: 'var(--surface)',
          border: '1px solid var(--line)',
          borderRadius: '10px',
        }}>
          Aucun plan de lancement. Créez-en un pour démarrer.
        </div>
      ) : (
        <div>
          <div className="xhero">
            <div data-testid="launch-countdown">
              <Countdown launchDate={activePlan?.launchDate ?? null} />
            </div>
            {timelineMilestones.length > 0 && (
              <div data-testid="launch-timeline">
                <MilestoneTimeline milestones={timelineMilestones} />
              </div>
            )}
          </div>
          {milestoneGroups.map((g) => (
            <MilestoneGroup
              key={g.milestoneOrder}
              milestone={g.milestone}
              milestoneOrder={g.milestoneOrder}
              tasks={g.tasks}
              launchDate={activePlan?.launchDate ?? null}
              onToggle={handleToggle}
              onEdit={handleEditTask}
              onDelete={handleDeleteTask}
              onAddTask={handleAddTask}
            />
          ))}
        </div>
      )}

      {showNewDialog && (
        <NewLaunchDialog
          onClose={() => setShowNewDialog(false)}
          onConfirm={handleCreatePlan}
          isPending={createPlan.isPending}
          error={dialogError}
          existingPlans={plans}
        />
      )}

      {editTask && (
        <EditTaskDialog
          state={editTask}
          onClose={() => setEditTask(null)}
          onSave={(data) => {
            if (editTask.task) {
              patchTask.mutate(
                { id: editTask.task.id, data },
                { onSuccess: () => setEditTask(null) },
              )
            } else {
              const existing = tasks.filter((t) => t.milestoneOrder === editTask.milestoneOrder)
              const nextSort = existing.length
              createTask.mutate(
                {
                  planId: activePlanId!,
                  milestone: editTask.milestone,
                  milestoneOrder: editTask.milestoneOrder,
                  title: (data as { title: string }).title,
                  detail: (data as { detail?: string | null }).detail ?? null,
                  dayOffset: (data as { dayOffset?: number | null }).dayOffset ?? null,
                  sortOrder: nextSort,
                },
                { onSuccess: () => setEditTask(null) },
              )
            }
          }}
        />
      )}

      {toast && (
        <div
          role="status"
          aria-live="polite"
          style={{
            position: 'fixed',
            bottom: '1.5rem',
            right: '1.5rem',
            background: 'var(--elevated)',
            border: '1px solid var(--line)',
            borderRadius: '8px',
            padding: '0.625rem 1rem',
            fontSize: '0.875rem',
            color: 'var(--fg)',
            zIndex: 2000,
          }}
        >
          {toast}
        </div>
      )}
    </div>
  )
}

// ── Composant interne dialog édition tâche ─────────────────────────────────

type EditTaskDialogProps = {
  state: EditTaskState
  onClose: () => void
  onSave: (data: { title?: string; detail?: string | null; dayOffset?: number | null }) => void
}

function EditTaskDialog({ state, onClose, onSave }: EditTaskDialogProps) {
  const [title, setTitle] = useState(state.task?.title ?? '')
  const [detail, setDetail] = useState(state.task?.detail ?? '')
  const [dayOffset, setDayOffset] = useState<string>(
    state.task?.dayOffset !== null && state.task?.dayOffset !== undefined
      ? String(state.task.dayOffset)
      : '',
  )

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    const parsed = dayOffset !== '' ? parseInt(dayOffset, 10) : null
    onSave({ title: title.trim(), detail: detail.trim() || null, dayOffset: parsed })
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={state.task ? 'Modifier la tâche' : 'Nouvelle tâche'}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.4)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose() }}
    >
      <div
        style={{
          background: 'var(--surface)',
          border: '1px solid var(--line)',
          borderRadius: '12px',
          padding: '1.5rem',
          width: '420px',
          maxWidth: '90vw',
        }}
      >
        <h2 style={{ fontSize: '1rem', fontWeight: 600, color: 'var(--fg)', marginBottom: '1.25rem' }}>
          {state.task ? 'Modifier la tâche' : 'Nouvelle tâche'}
        </h2>
        <p style={{ fontSize: '0.8125rem', color: 'var(--muted)', marginBottom: '1rem' }}>
          Jalon : <strong>{state.milestone}</strong>
        </p>
        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '1rem' }}>
            <label
              htmlFor="task-title"
              style={{ display: 'block', fontSize: '0.8125rem', color: 'var(--muted)', marginBottom: '0.375rem' }}
            >
              Titre
            </label>
            <input
              id="task-title"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
              style={inputStyle}
            />
          </div>
          <div style={{ marginBottom: '1rem' }}>
            <label
              htmlFor="task-detail"
              style={{ display: 'block', fontSize: '0.8125rem', color: 'var(--muted)', marginBottom: '0.375rem' }}
            >
              Détail (optionnel)
            </label>
            <textarea
              id="task-detail"
              value={detail}
              onChange={(e) => setDetail(e.target.value)}
              rows={3}
              style={{ ...inputStyle, resize: 'vertical' }}
            />
          </div>
          <div style={{ marginBottom: '1.25rem' }}>
            <label
              htmlFor="task-offset"
              style={{ display: 'block', fontSize: '0.8125rem', color: 'var(--muted)', marginBottom: '0.375rem' }}
            >
              Décalage en jours (vide = flottant)
            </label>
            <input
              id="task-offset"
              type="number"
              value={dayOffset}
              onChange={(e) => setDayOffset(e.target.value)}
              placeholder="ex. -25 ou 0"
              style={inputStyle}
            />
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
            <button type="button" onClick={onClose} style={btnSecondary}>Annuler</button>
            <button type="submit" disabled={!title.trim()} style={btnPrimary}>Enregistrer</button>
          </div>
        </form>
      </div>
    </div>
  )
}

// ── Styles partagés ────────────────────────────────────────────────────────

const btnPrimary: React.CSSProperties = {
  background: 'var(--arancio)',
  color: '#fff',
  border: 'none',
  borderRadius: '6px',
  padding: '0.4375rem 1rem',
  fontSize: '0.875rem',
  fontWeight: 600,
  fontFamily: 'inherit',
  cursor: 'pointer',
}

const btnSecondary: React.CSSProperties = {
  background: 'var(--elevated)',
  color: 'var(--fg)',
  border: '1px solid var(--line)',
  borderRadius: '6px',
  padding: '0.4375rem 0.875rem',
  fontSize: '0.875rem',
  fontFamily: 'inherit',
  cursor: 'pointer',
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '0.4375rem 0.625rem',
  background: 'var(--bg)',
  border: '1px solid var(--line)',
  borderRadius: '6px',
  fontSize: '0.875rem',
  color: 'var(--fg)',
  fontFamily: 'inherit',
  boxSizing: 'border-box',
}
